import React, {
  Dispatch,
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
} from 'react';
import {
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSelector } from 'react-redux';
import Colors from '../../../constants/Colors';
import getMyTickets from '../../../requests/Tickets/GetMyTickets';
import getTickets from '../../../requests/Tickets/GetTickets';
import PressableStyled from '../../../components/PressableStyled';

// eslint-disable-next-line no-shadow
export enum OpenTicketsShown {
  // eslint-disable-next-line no-unused-vars
  OPEN = 'open',
  // eslint-disable-next-line no-unused-vars
  OPEN_IN_PROGRESS = 'openInProgress',
  // eslint-disable-next-line no-unused-vars
  OPEN_OVERDUE = 'openOverdue',
  // eslint-disable-next-line no-unused-vars
  OPEN_ASSIGNED_TO_ME = 'openAssignedToMe',
  // eslint-disable-next-line no-unused-vars
  OPEN_ASSIGNED_TO_ME_OVERDUE = 'openAssignedToMeOverdue',
}

export interface OpenTicketStatistics {
  all: number;
  open: number;
  openInProgress?: number;
  openOverdue: number;
  openAssignedToMe: number;
  openAssignedToMeOverdue: number;
}

export interface QuickFiltersRef {
  refresh: () => void;
}

interface QuickFiltersProps {
  openTicketsSelection: OpenTicketsShown | null,
  setOpenTicketsSelection: Dispatch<SetStateAction<OpenTicketsShown | null>>,
  counts: OpenTicketStatistics,
  setCounts: Dispatch<SetStateAction<OpenTicketStatistics>>,
}

interface QuickFilterButtonProps {
  label: string,
  count: number,
  selected?: boolean,
  overdue?: boolean,
  onPress: () => void,
  testID: string,
}

const QuickFilterButton = ({
  label,
  count,
  selected,
  overdue,
  onPress,
  testID,
}: QuickFilterButtonProps) => {
  const countColor = overdue && count > 0
    ? Colors.light.hereworksRed500
    : Colors.light.hereworksBlack600;

  return <PressableStyled
    onPress={onPress}
    style={[
      styles.filterButton,
      selected && styles.filterButtonSelected,
    ]}
    testID={testID}
  >
    <Text style={[styles.filterLabel, selected && styles.filterLabelSelected]}>{label}</Text>
    <View style={[
      styles.countBadge,
      overdue && styles.countBadgeOverdue,
      selected && styles.countBadgeSelected,
    ]}
    >
      <Text style={[styles.countText, { color: countColor }]}>{count ?? 0}</Text>
    </View>
  </PressableStyled>;
};

const QuickFilters = forwardRef<QuickFiltersRef, QuickFiltersProps>(({
  openTicketsSelection,
  setOpenTicketsSelection,
  counts,
  setCounts,
}: QuickFiltersProps, ref) => {
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);

  const refresh = () => {
    if (!selectedBuildingId) return;
    Promise.all([
      getMyTickets(selectedBuildingId),
      getTickets({
        buildingId: selectedBuildingId,
        limit: 1,
      }),
      getTickets({
        buildingId: selectedBuildingId,
        inprogress: true,
        reported: false,
        limit: 1,
      }),
    ]).then(([myTicketsResponse, allTicketsResponse, inProgressTicketsResponse]) => {
      if (!(myTicketsResponse && !myTicketsResponse.isError)) return;

      const allCount = allTicketsResponse?.count ?? allTicketsResponse?.statistics?.totalTickets;
      const inProgressCount = inProgressTicketsResponse?.count
        ?? inProgressTicketsResponse?.statistics?.totalTickets;

      setCounts({
        ...myTicketsResponse,
        open: myTicketsResponse.open,
        all: allCount,
        openInProgress: inProgressCount ?? myTicketsResponse.open,
      });
    });
  };

  useImperativeHandle(ref, () => ({ refresh }));

  useEffect(() => {
    refresh();
  }, [selectedBuildingId]);

  const toggleFilter = (filter: OpenTicketsShown) => {
    setOpenTicketsSelection((previous) => (previous === filter ? null : filter));
  };

  return <View style={styles.container}>
    <QuickFilterButton
      label='All'
      count={counts?.all ?? 0}
      selected={!openTicketsSelection}
      onPress={() => setOpenTicketsSelection(null)}
      testID='quick-filter-all-btn'
    />
    <QuickFilterButton
      label='Open'
      count={counts?.open ?? 0}
      selected={openTicketsSelection === OpenTicketsShown.OPEN}
      onPress={() => toggleFilter(OpenTicketsShown.OPEN)}
      testID='quick-filter-open-btn'
    />
    <QuickFilterButton
      label='In Progress'
      count={counts?.openInProgress ?? counts?.open ?? 0}
      selected={openTicketsSelection === OpenTicketsShown.OPEN_IN_PROGRESS}
      onPress={() => toggleFilter(OpenTicketsShown.OPEN_IN_PROGRESS)}
      testID='quick-filter-in-progress-btn'
    />
    <QuickFilterButton
      label='Overdue'
      count={counts?.openOverdue ?? 0}
      overdue
      selected={openTicketsSelection === OpenTicketsShown.OPEN_OVERDUE}
      onPress={() => toggleFilter(OpenTicketsShown.OPEN_OVERDUE)}
      testID='quick-filter-overdue-btn'
    />
    <QuickFilterButton
      label='Assigned to me'
      count={counts?.openAssignedToMe ?? 0}
      selected={openTicketsSelection === OpenTicketsShown.OPEN_ASSIGNED_TO_ME}
      onPress={() => toggleFilter(OpenTicketsShown.OPEN_ASSIGNED_TO_ME)}
      testID='quick-filter-assigned-btn'
    />
    <QuickFilterButton
      label='Assigned overdue'
      count={counts?.openAssignedToMeOverdue ?? 0}
      overdue
      selected={openTicketsSelection === OpenTicketsShown.OPEN_ASSIGNED_TO_ME_OVERDUE}
      onPress={() => toggleFilter(OpenTicketsShown.OPEN_ASSIGNED_TO_ME_OVERDUE)}
      testID='quick-filter-assigned-overdue-btn'
    />
  </View>;
});

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: Colors.light.hereworksWhite100,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  filterButtonHovered: {
    borderColor: Colors.light.hereworksBlue200,
  },
  filterButtonSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
    borderColor: Colors.light.hereworksDarkBlue,
  },
  filterLabel: {
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 14,
  },
  filterLabelSelected: {
    color: Colors.light.hereworksWhite500,
  },
  countBadge: {
    marginLeft: 8,
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: Colors.light.hereworksWhite500,
    minWidth: 26,
    alignItems: 'center',
  },
  countBadgeOverdue: {
    borderColor: Colors.light.hereworksLightPink,
    backgroundColor: Colors.light.hereworksRed50,
  },
  countBadgeSelected: {
    borderColor: Colors.light.hereworksBlue200,
  },
  countText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 14,
  },
});

export default QuickFilters;
